import type { ReactElement } from 'react';
import { Badge, Text, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import { brandName, type Mailbox } from '@gandhi/mail-protocol';
import { brandAccent } from '@gandhi-group/ui';
import { mailboxesByBrand } from '../state/mail-store.js';
import { useController, useMail } from '../state/context.js';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px 8px' },
  group: { display: 'flex', flexDirection: 'column', gap: '4px' },
  heading: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 8px',
    textTransform: 'uppercase',
    fontSize: tokens.fontSizeBase100,
    letterSpacing: '0.08em',
    color: tokens.colorNeutralForeground3,
  },
  rule: { flex: 1, height: '2px', borderRadius: '2px' },
  item: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    width: '100%',
    padding: '8px 10px',
    borderRadius: tokens.borderRadiusMedium,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    color: tokens.colorNeutralForeground1,
    ':hover': { background: tokens.colorNeutralBackground1Hover },
  },
  active: { background: tokens.colorBrandBackground2, fontWeight: tokens.fontWeightSemibold },
});

/**
 * Two brands, two lists, two headings. Never a single flat list of addresses, and
 * never a From picker: choosing here is choosing which mailbox you are working in,
 * and that is what decides who anything sent next comes from.
 */
export function MailboxSwitcher(): ReactElement {
  const styles = useStyles();
  const controller = useController();
  const groups = useMail(mailboxesByBrand);
  const activeMailboxId = useMail((s) => s.activeMailboxId);
  const mode = useMail((s) => s.themeMode);

  return (
    <nav className={styles.root} aria-label="Mailboxes">
      {groups.map(({ brandId, mailboxes }) => (
        <section
          key={brandId}
          className={styles.group}
          aria-label={`${brandName(brandId)} mailboxes`}
          data-brand-group={brandId}
        >
          <h2 className={styles.heading}>
            <span>{brandName(brandId)}</span>
            <span
              className={styles.rule}
              style={{ background: brandAccent(brandId, mode) }}
              aria-hidden="true"
            />
          </h2>
          {mailboxes.map((mailbox) => (
            <MailboxButton
              key={mailbox.id}
              mailbox={mailbox}
              active={mailbox.id === activeMailboxId}
              onSelect={() => void controller.selectMailbox(mailbox.id)}
              className={`${styles.item} ${mailbox.id === activeMailboxId ? styles.active : ''}`}
            />
          ))}
        </section>
      ))}
    </nav>
  );
}

function MailboxButton(props: {
  mailbox: Mailbox;
  active: boolean;
  className: string;
  onSelect: () => void;
}): ReactElement {
  const { mailbox, active, className, onSelect } = props;
  const label = `${brandName(mailbox.brandId)} mailbox, ${mailbox.address}`;
  const button = (
    <button
      type="button"
      className={className}
      aria-current={active ? 'true' : undefined}
      aria-label={label}
      data-mailbox-id={mailbox.id}
      data-brand={mailbox.brandId}
      onClick={onSelect}
    >
      <Text truncate wrap={false}>
        {mailbox.address}
      </Text>
      {!mailbox.canSend && (
        <Badge appearance="outline" color="warning">
          Receive only
        </Badge>
      )}
    </button>
  );

  return mailbox.canSend ? (
    button
  ) : (
    <Tooltip
      content={`${mailbox.address} can receive but not send. The ${brandName(mailbox.brandId)} brand grant was removed.`}
      relationship="description"
    >
      {button}
    </Tooltip>
  );
}
